import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DocumentFile } from "@/contexts/DocumentContext";
import { FileText, Download, Eye } from "lucide-react";

interface DocumentViewerProps {
  document: DocumentFile;
  trigger?: React.ReactNode;
}

export function DocumentViewer({ document: doc, trigger }: DocumentViewerProps) {
  const isImage = doc.type.startsWith("image/");
  const isPdf = doc.type === "application/pdf";

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = doc.dataUrl;
    link.download = doc.name;
    link.click();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <button className="text-primary hover:text-primary/80 transition-colors">
            <Eye className="h-4 w-4" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              {doc.name}
            </DialogTitle>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto rounded-lg border border-border bg-muted/20 min-h-[400px]">
          {isImage && (
            <div className="flex items-center justify-center p-4 h-full">
              <img
                src={doc.dataUrl}
                alt={doc.name}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
              />
            </div>
          )}
          {isPdf && (
            <iframe
              src={doc.dataUrl}
              title={doc.name}
              className="w-full h-[70vh] rounded-lg"
            />
          )}
          {!isImage && !isPdf && (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
              <FileText className="h-16 w-16 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Preview not available for this file type</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Download to View
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-2 flex-shrink-0">
          <span>Uploaded by {doc.uploadedByName}</span>
          <span>•</span>
          <span>{new Date(doc.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
          <span>•</span>
          <span>{(doc.size / 1024).toFixed(1)} KB</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
